
import { Component, Inject, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import NavHeader from "@/components/NavHeader.vue";
import { ViewMode, ViewObject } from "@/store/views/types.ts";
import ViewShare from "@/components/views/ViewShare.vue";
import { last } from "lodash";
import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";
import Sessions from "@/components/Sessions.vue";
import { views } from "../../store/views";
import { BFormInput } from "bootstrap-vue";
import viewsApi from "../../services/api.views.service";
import ViewsEventBus from "@/services/views/viewsEventBus";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { Dropdown, DropdownItem } from "@sureview/vuejs-sv-dropdown";
import { cloneDeep } from "lodash";

const ViewState = namespace("views");

@Component({
	components: {
		"nav-header": NavHeader,
		session: Sessions,
		"view-share": ViewShare,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"sv-dropdown": Dropdown,
		"sv-dropdown-item": DropdownItem
	},
	mixins: [validationMixin],
	validations: {
		title: {
			required,
			minLength: minLength(1)
		}
	}
})
export default class ViewHeader extends Vue {
	public $refs!: {
		viewTitleInput: BFormInput;
		viewsScrollbar: any;
	};

	@ViewState.Action loadUserViews: any;
	@ViewState.Action loadAdhocUserView: any;
	@ViewState.Action loadViews: any;
	@ViewState.Action loadViewUpdateTime: any;
	@ViewState.Action loadViewContentUpdateTime: any;
	@ViewState.Action("saveView") save: any;
	@ViewState.Action("addUserView") add: any;
	@ViewState.Action("deleteView") delete: any;
	@ViewState.Action("removeUserView") remove: any;
	@ViewState.Action getViewDetails: any;
	@ViewState.Action getViewProperties: any;
	@ViewState.Action loadUpdatedCellContent: any;
	@ViewState.Action loadPluginTypes: any;
	@ViewState.Action toggleLock: any;

	@ViewState.Mutation setSelectedView: any;
	@ViewState.Mutation setSelectedViewTitle: any;
	@ViewState.Mutation setSelectedViewCells: any;
	@ViewState.Mutation setViewDimensions: any;
	@ViewState.Mutation setViewMode: any;
	@ViewState.Mutation setSelectedTileType: any;
	@ViewState.Mutation setSideBarExtended: any;
	@ViewState.Mutation setSelectedViewPublic: any;

	@ViewState.Getter("getViews") views: any;
	@ViewState.Getter("getUserViews") userViews: any;
	@ViewState.Getter("getSelectedView") selectedView: ViewObject;
	@ViewState.Getter("getPluginTypes") tileTypes: any;
	@ViewState.Getter getSelectedTileType: any;
	@ViewState.Getter getSideBarExtended: boolean;
	@ViewState.Getter getSideBarPinned: boolean;
	@ViewState.Getter getViewMode: ViewMode;

	@Getter("getUserId") public currentUserId!: number;
	@Getter getUserGroupId: number;
	@Getter getPermissions: any;
	@Getter isFullUser;

	@Inject("viewsEventBus") private eventBus!: ViewsEventBus;

	public viewFilter: string = null;
	public viewCellNextId: number = 20;
	private resizeHoverWidth: number = 0;
	private resizeHoverHeight: number = 0;
	private maxWidth: number = 5;
	private maxHeight: number = 5;
	private newView: ViewObject = null;
	private titleError: boolean = false;
	private isDeleteConfirmationShown: boolean = false;
	private checkChangesInterval = null;
	private layoutExpired: boolean = false;
	private showTooltip: boolean = true;
	private isAdding: boolean = false;
	private errorMessage: string = "";
	private isShareModalOpen: boolean = false;
	private viewToShare: any = {};
	private showMaxPluginError: boolean = false;
	private contentLastUpdated: number = null;
	public originalView: any = null;
	public modalShow = false;
	public viewToLock: ViewObject = null;

	private get currentUserViews() {
		if (this.newView) {
			return this.userViews.concat([this.newView]);
		}

		return this.userViews;
	}

	public get availableViews() {
		if (this.viewFilter) {
			return this.views.filter(view => view.title.toLowerCase().includes(this.viewFilter));
		}

		return this.views;
	}

	private get userHasEditPermission() {
		if (
			this.selectedView &&
			((!this.selectedView.public && this.selectedView.ownerUserId == this.currentUserId) ||
				(this.selectedView.public && (this.getPermissions.canEditPublicView || this.getPermissions.isSystemAdmin)))
		) {
			return true;
		}

		return false;
	}
	private get userHasClonePermission() {
		return this.getPermissions.canCloneViews;
	}

	private get userHasLockedPermission(){
		return this.getPermissions.canLockViews;
	}

	public get editing() {
		return this.getViewMode === ViewMode.Edit;
	}

	public get sideBarExtended() {
		return this.getSideBarExtended;
	}

	public set sideBarExtended(state: boolean) {
		this.setSideBarExtended(state);
	}

	public get title() {
		if (!this.selectedView) {
			return "";
		}

		return this.selectedView.title;
	}

	public set title(value: string) {
		this.setSelectedViewTitle(value);
	}

	public get width() {
		if (!this.selectedView) {
			return 0;
		}

		return this.selectedView.width;
	}

	public get height() {
		if (!this.selectedView) {
			return 0;
		}

		return this.selectedView.height;
	}

	public get selectedTileType() {
		return this.getSelectedTileType || {};
	}

	public set selectedTileType(tileType: any) {
		this.setSelectedTileType(tileType);
	}

	public onFinishAdd(eventData: { tileType: number }) {
		this.setTileType(eventData.tileType);
	}

	public async mounted() {
		this.eventBus.$on("finishedAdd", this.onFinishAdd);

		if (this.isFullUser) {
			// load the views the user would like to have on screen
			await this.loadViews();
			await this.loadUserViews();
		} else {
			// the user is an adhoc user, get the view for this adhoc session
			await this.loadAdhocUserView(this.$route.query.sessionId);
		}

		// when user recieves a share view email, they will be given a link to the view by specific id
		if (this.$route.query.id) {
			await this.changeSelectedView(this.$route.query.id);
			return;
		}

		// set the current view to to be the first in the list
		if (this.currentUserViews.length > 0) {
			await this.changeSelectedView(this.currentUserViews[0].viewId);
		}

		window.addEventListener("resize", this.onWindowResized);
	}

	public destroyed() {
		window.removeEventListener("resize", this.onWindowResized);
	}

	public onWindowResized() {
		this.$refs.viewsScrollbar.update();
	}

	public async beforeDestroyed() {
		clearInterval(this.checkChangesInterval);
	}

	@Watch("$route.query.id")
	async onRouteChange(newViewId, oldViewId) {
		await this.changeSelectedView(newViewId);
	}

	public cellBackground(x: number, y: number) {
		if (y <= this.resizeHoverHeight && x <= this.resizeHoverWidth) {
			if (y <= this.height && x <= this.width) {
				return {
					backgroundColor: "#9ccbf0"
				};
			} else {
				return {
					backgroundColor: "#aaa"
				};
			}
		} else if (y <= this.height && x <= this.width) {
			return {
				backgroundColor: "#2196F3"
			};
		} else {
			return {};
		}
	}

	public cellHover(x: number, y: number) {
		this.resizeHoverWidth = x;
		this.resizeHoverHeight = y;
	}

	public resizeView(x: number, y: number) {
		this.setViewDimensions({ x: x, y: y });
	}

	public async changeSelectedView(viewId) {
		if (this.editing) {
			await this.cancelViewEdit();
		}

		// Clear our update poll interval
		clearInterval(this.checkChangesInterval);

		// set ViewMode
		await this.setViewMode(ViewMode.None);

		// Get View details such as cells, height, width
		await this.getViewDetails(viewId);

		// Reset the last time our content was updated
		this.contentLastUpdated = Date.now();

		// Setup our interval for checking layout/content changes
		this.checkChangesInterval = setInterval(() => {
			this.checkLayoutUpdated();
			this.checkContentUpdated();
		}, 5000);
	}

	public async checkLayoutUpdated() {
		if (this.selectedView == null || this.selectedView.viewId == null) return;
		const viewUpdatedLast = await this.loadViewUpdateTime(this.selectedView.viewId);
		const lastUpdatedTime = Date.parse(viewUpdatedLast);
		const viewLoadedTime = Date.parse(this.selectedView.dateLoaded);

		if (lastUpdatedTime > viewLoadedTime) {
			if (this.editing) {
				if (!this.layoutExpired) {
					const timeDiff = -(lastUpdatedTime - new Date().getTime()) / 1000;
					this.$notify({
						group: "expired",
						duration: -1,
						data: {
							timeDiff
						}
					});
				}
			} else {
				await this.refreshLayout();
			}

			this.layoutExpired = true;
		} else {
			this.layoutExpired = false;
		}
	}

	public async checkContentUpdated() {
		const viewContentUpdatedLast = await this.loadViewContentUpdateTime(this.selectedView.viewId);
		const lastUpdatedTime = Date.parse(viewContentUpdatedLast);

		if (lastUpdatedTime > this.contentLastUpdated) {
			await this.loadUpdatedCellContent(this.selectedView.viewId);
			this.contentLastUpdated = Date.now();
		}
	}

	public async editView(view: any) {
		this.getViewDetails(view.viewId);
		await this.setViewMode(ViewMode.Edit);
		await this.setSelectedView(view);

		this.$refs.viewTitleInput.focus();
	}

	public async createView() {
		this.newView = {
			viewId: null,
			title: "",
			width: 5,
			height: 5,
			public: false,
			cells: [],
			groupId: this.getUserGroupId,
			ownerUserId: this.currentUserId,
			locked: false
		};

		clearInterval(this.checkChangesInterval);
		await this.setSelectedView(this.newView);
		await this.setViewMode(ViewMode.Edit);

		// Set our title to be focused
		this.$refs.viewTitleInput.focus();
	}

	private openShareModal(view: any) {
		this.viewToShare = view;
		this.isShareModalOpen = true;
	}

	public async saveView() {
		try {
			clearInterval(this.checkChangesInterval);
			this.isAdding = true;
			this.$v.$touch();
			if (this.$v.title.$error) {
				this.errorMessage = "You must set a title";
				return;
			}

			if(this.selectedView.cells == null){
				this.selectedView.cells = this.newView.pages[0].cells;
			}
			const savedView = await this.save(this.selectedView);

			if (savedView.viewId && !savedView.error) {
				await this.endEditing();
				this.titleError = false;
				await this.refreshViewsLists();
				await this.changeSelectedView(savedView.viewId);

				if (this.newView) {
					this.newView = null;
				}
			} else {
				this.titleError = true;
				this.errorMessage = savedView.error;
			}
		} finally {
			this.isAdding = false;
		}

	}

	public async cancelViewEdit() {
		await this.refreshViewsLists();
		await this.setViewMode(ViewMode.None);

		if (this.newView) {
			if (this.currentUserViews.length > 0) {
				await this.changeSelectedView(this.currentUserViews[0].viewId);
			} else {
				await this.setSelectedView(null);
			}
			this.newView = null;
		} else {
			try {
				await this.changeSelectedView(
					this.currentUserViews.find(view => view.viewId == this.selectedView.viewId).viewId
				);
			} catch {
				await this.changeSelectedView(this.currentUserViews[0].viewId);
			}
		}

		await this.endEditing();
	}

	public async endEditing() {
		await this.setViewMode(ViewMode.None);
		this.selectedTileType = null;
		this.$v.$reset();
		this.titleError = false;
	}

	public async handleCurrentView() {
		// after removing a view set the selected view to be the first available, if any
		if (this.currentUserViews.length > 0) {
			await this.changeSelectedView(this.currentUserViews[0].viewId);
		} else {
			await this.setViewMode(ViewMode.None);
			await this.setSelectedView(null);
			clearInterval(this.checkChangesInterval);
		}
	}

	public async deleteView() {
		this.isDeleteConfirmationShown = false;
		await this.delete(this.selectedView.viewId);
		await this.refreshViewsLists();
		await this.handleCurrentView();
		await this.endEditing();
	}

	public async addView(viewId) {
		await this.add(viewId);
		await this.refreshViewsLists();
		await this.setViewMode(ViewMode.None);
		await this.changeSelectedView(viewId);
	}


	public async cloneView(viewId) {
		this.originalView = await this.getViewProperties(viewId);

		var copiedCells = [];
		var tempId = -Math.floor(100000 + Math.random() * 900000);

		this.originalView.pages[0].cells.forEach(cell => {
			copiedCells.push({
				viewCellId: -Math.floor(100000 + Math.random() * 900000),
				width: cell.columnSpan,
				height: cell.rowSpan,
				pluginTypeId: cell.pluginId,
				x: cell.x,
				y: cell.y,
				contents: cell.contents
			});
		});

		this.newView = {
			viewId: null,
			title: null,
			width: this.originalView.pages[0].rows,
			height: this.originalView.pages[0].columns,
			public: false,
			groupId: this.getUserGroupId,
			ownerUserId: this.currentUserId,
			cells: copiedCells,
			pages: this.originalView.pages,
			locked: false
		};


		clearInterval(this.checkChangesInterval);

		await this.setSelectedView(this.newView);
		await this.setViewMode(ViewMode.Edit);

		// Set our title to be focused
		this.$refs.viewTitleInput.focus();
	}

	public async lockClicked(view){
		this.viewToLock = view;
		this.modalShow = true;
	}

	public async lockView(){
		await this.toggleLock(this.viewToLock.viewId);
		await this.refreshViewsLists();
		await this.changeSelectedView(this.selectedView.viewId);
		this.modalShow = false;
	}

	public async hideLockModal(){
		this.modalShow = false;
	}

	public async removeView(viewId) {
		await this.remove(viewId);
		await this.refreshViewsLists();
		await this.handleCurrentView();
	}

	public async refreshViewsLists() {
		await this.loadViews();
		await this.loadUserViews();
	}

	public async refreshLayout() {
		await this.changeSelectedView(this.selectedView.viewId);
	}

	public setTileType(tileType: any) {
		this.showMaxPluginError = false;
		if (this.selectedTileType === tileType) {
			this.selectedTileType = null;
		} else if (
			!tileType.maxInstances ||
			tileType.maxInstances > this.selectedView.cells.filter(cell => cell.pluginTypeId == tileType.id).length
		) {
			this.selectedTileType = tileType;
		} else {
			this.showMaxPluginError = true;
		}
	}
}
